import React from 'react';
import './Community.css';

export default function Community() {
  return (
    <div className="community-container">
      <header className="community-headers">
        <h1>Community</h1>
        <p className="last-updated">Join the Humanex Community</p>
      </header>

      <div className="community-content">
        <section className="community-section">
          <h2>1. Welcome to Our Community</h2>
          <p>
            Welcome to the Humanex Community—a vibrant ecosystem of HR professionals, business leaders, and innovators who are transforming the way organizations manage their human resources. Our community is built on the principles of collaboration, knowledge sharing, and continuous learning. Whether you're a seasoned HR director or just starting your journey in people management, you'll find valuable resources, insights, and connections here.
          </p>
        </section>

        <section className="community-section">
          <h2>2. Community Guidelines</h2>
          <h3>2.1 Be Respectful and Professional</h3>
          <p>
            Treat all community members with respect, courtesy, and professionalism. We embrace diversity of thought, experience, and background. Discriminatory language, harassment, or hostile behavior will not be tolerated. Remember that behind every username is a real person deserving of dignity and consideration.
          </p>
          
          <h3>2.2 Share Knowledge Generously</h3>
          <p>
            Our community thrives when members share their expertise, experiences, and insights. Whether it's answering questions, sharing best practices, or offering constructive feedback, your contributions help everyone grow. Don't hesitate to ask questions—there are no "silly" questions here, only opportunities to learn.
          </p>

          <h3>2.3 Maintain Confidentiality</h3>
          <p>
            When discussing workplace situations or seeking advice, please maintain confidentiality by anonymizing sensitive information. Never share personally identifiable information about employees, candidates, or companies without explicit permission. Respect the privacy of others and expect the same in return.
          </p>

          <h3>2.4 Stay On Topic</h3>
          <p>
            Keep discussions relevant to HR, people management, and organizational development. While some off-topic conversation builds community, please use appropriate channels for general chat. This helps everyone find the information they need quickly and keeps our community focused and valuable.
          </p>

          <h3>2.5 No Spam or Self-Promotion</h3>
          <p>
            Our community is not a marketplace for unsolicited advertising or self-promotion. While sharing relevant resources or mentioning your company in context is acceptable, blatant marketing, spam, or excessive self-promotion will be removed. If you're unsure whether something is appropriate, ask a moderator first.
          </p>
        </section>

        <section className="community-section">
          <h2>3. Community Forums and Channels</h2>
          <h3>3.1 Discussion Forums</h3>
          <p>
            Our discussion forums cover a wide range of HR topics where you can engage in threaded conversations, ask questions, and share experiences. Popular forums include:
          </p>
          <ul>
            <li>Talent Acquisition and Recruiting Strategies</li>
            <li>Employee Engagement and Retention</li>
            <li>Performance Management and Reviews</li>
            <li>Compensation and Benefits</li>
            <li>HR Technology and Automation</li>
            <li>Diversity, Equity, and Inclusion</li>
            <li>Learning and Development</li>
            <li>HR Compliance and Legal Issues</li>
            <li>Remote and Hybrid Work Management</li>
            <li>Organizational Culture and Change Management</li>
          </ul>

          <h3>3.2 Regional and Industry Groups</h3>
          <p>
            Connect with HR professionals in your geographic region or specific industry. These focused groups allow for more relevant discussions about local regulations, industry-specific challenges, and regional networking opportunities.
          </p>

          <h3>3.3 Special Interest Groups</h3>
          <p>
            Join special interest groups focused on specific areas such as HR analytics, startups and small business HR, global mobility, employee wellness, or emerging technologies in HR. These communities dive deep into specialized topics.
          </p>
        </section>

        <section className="community-section">
          <h2>4. Events and Networking</h2>
          <h3>4.1 Virtual Meetups</h3>
          <p>
            Join our regular virtual meetups where community members gather to discuss trending topics, share case studies, and network with peers. These informal sessions provide opportunities to learn from others' experiences and build meaningful professional relationships.
          </p>

          <h3>4.2 Webinars and Workshops</h3>
          <p>
            Participate in expert-led webinars and hands-on workshops covering the latest HR trends, best practices, and platform features. Our educational sessions are designed to help you maximize your HR effectiveness and stay ahead of industry developments.
          </p>

          <h3>4.3 Annual Community Conference</h3>
          <p>
            Our flagship annual conference brings together HR professionals from around the world for multi-day learning, networking, and celebration. Featuring keynote speakers, breakout sessions, product announcements, and evening social events, it's the highlight of our community calendar.
          </p>

          <h3>4.4 Local Chapters</h3>
          <p>
            Connect with community members in your area through local chapter events. These in-person gatherings range from casual coffee meetups to formal networking dinners, providing opportunities to build strong local professional networks.
          </p>
        </section>

        <section className="community-section">
          <h2>5. Resources and Support</h2>
          <h3>5.1 Knowledge Base</h3>
          <p>
            Access our comprehensive knowledge base filled with articles, guides, templates, and best practices contributed by community members and HR experts. Topics range from onboarding checklists to performance review frameworks to compliance documentation.
          </p>

          <h3>5.2 Template Library</h3>
          <p>
            Download and customize HR templates shared by the community including job descriptions, offer letters, policy documents, employee handbooks, performance review forms, and more. Save time by leveraging tested resources rather than starting from scratch.
          </p>

          <h3>5.3 Expert Office Hours</h3>
          <p>
            Get direct access to HR experts, employment law specialists, and product specialists during regular office hours. Bring your specific questions or challenges and receive personalized guidance from seasoned professionals.
          </p>

          <h3>5.4 Peer Mentorship Program</h3>
          <p>
            Connect with experienced HR professionals through our mentorship program. Whether you're seeking career guidance, advice on specific challenges, or looking to give back by mentoring others, our structured program facilitates meaningful professional relationships.
          </p>
        </section>

        <section className="community-section">
          <h2>6. Contributing to the Community</h2>
          <h3>6.1 Become a Community Leader</h3>
          <p>
            Community leaders help moderate discussions, organize events, and foster engagement within specific forums or groups. If you're passionate about building community and helping others, consider applying to become a community leader.
          </p>

          <h3>6.2 Share Your Expertise</h3>
          <p>
            Write articles, create video tutorials, develop templates, or lead workshops to share your knowledge with the community. Expert contributors receive recognition, build their professional brand, and help advance the HR profession.
          </p>

          <h3>6.3 Organize Local Events</h3>
          <p>
            Take initiative to organize local chapter events in your area. We provide support, resources, and funding for community-led events that bring members together for learning and networking.
          </p>

          <h3>6.4 Provide Product Feedback</h3>
          <p>
            Your insights and experiences using Humanex are invaluable in shaping the product's future. Participate in beta testing, share feature requests, report bugs, and contribute to product discussions to help us build better solutions for everyone.
          </p>
        </section>

        <section className="community-section">
          <h2>7. Recognition and Rewards</h2>
          <h3>7.1 Community Points and Badges</h3>
          <p>
            Earn points and badges for active participation including posting helpful answers, contributing resources, attending events, and helping other members. Unlock special privileges and recognition as you advance through community levels.
          </p>

          <h3>7.2 Member Spotlight</h3>
          <p>
            Outstanding community members are regularly featured in our member spotlight series, showcasing their contributions, sharing their stories, and celebrating their achievements. This recognition highlights the diverse talent within our community.
          </p>

          <h3>7.3 Annual Community Awards</h3>
          <p>
            Each year, we recognize exceptional community members with awards in categories such as Most Helpful Member, Best Contributor, Community Leader of the Year, and Innovation Champion. Award recipients receive special recognition and exclusive benefits.
          </p>
        </section>

        <section className="community-section">
          <h2>8. Best Practices for Engagement</h2>
          <p>
            To get the most value from our community, consider these best practices:
          </p>
          <ul>
            <li>Complete your profile with your role, interests, and expertise to help others connect with you</li>
            <li>Introduce yourself in the welcome forum when you first join</li>
            <li>Use search before posting to see if your question has been answered</li>
            <li>Provide context when asking questions to help others provide better answers</li>
            <li>Mark helpful responses and share appreciation for those who assist you</li>
            <li>Follow topics and forums relevant to your interests for regular updates</li>
            <li>Set notification preferences to stay informed without being overwhelmed</li>
            <li>Participate regularly—even small contributions build community connections</li>
            <li>Share both successes and challenges; vulnerability builds trust</li>
            <li>Pay it forward by helping others when you have knowledge to share</li>
          </ul>
        </section>

        <section className="community-section">
          <h2>9. Code of Conduct</h2>
          <h3>9.1 Expected Behavior</h3>
          <p>
            All community members are expected to demonstrate empathy, respect diversity of perspectives, accept constructive criticism gracefully, focus on what's best for the community, and show courtesy and professionalism in all interactions.
          </p>

          <h3>9.2 Unacceptable Behavior</h3>
          <p>
            The following behaviors are unacceptable and may result in removal from the community: harassment of any kind, discriminatory language or actions, trolling or deliberately inflammatory comments, sharing private information without consent, sustained disruption of discussions, spam or commercial solicitation, and impersonation of others.
          </p>

          <h3>9.3 Reporting and Enforcement</h3>
          <p>
            If you experience or witness behavior that violates our code of conduct, please report it to community moderators immediately. All reports are handled confidentially. Enforcement actions range from warnings to temporary suspensions to permanent bans, depending on the severity and frequency of violations.
          </p>
        </section>

        <section className="community-section">
          <h2>10. Privacy and Data Protection</h2>
          <p>
            Your privacy is important to us. Community discussions are visible to all members, so please be mindful of what you share. Never post sensitive personal or company information. We do not sell or share member data with third parties. For more details, please review our Privacy Policy.
          </p>
        </section>

        <section className="community-section">
          <h2>11. Getting Help</h2>
          <p>
            Need assistance with the community platform or have questions about community programs? Our community support team is here to help:
          </p>
          <div className="contact-info">
            <p><strong>Community Support Email:</strong> support@devopstrioglobal.com</p>
            <p><strong>Forum Help Desk:</strong> Available in the "Community Support" form</p>
            <p><strong>Report an Issue:</strong> Use the "Report" button on any post or contact moderators directly</p>
            <p><strong>General Inquiries:</strong> +1 (555) 123-4567</p>
          </div>
        </section>

        <section className="community-section">
          <h2>12. Join Us Today</h2>
          <p>
            Ready to connect with fellow HR professionals and take your career to the next level? Creating your community account is simple and free. Sign up today to access forums, attend events, download resources, and become part of a supportive network dedicated to advancing the HR profession. Together, we're building the future of human resources.
          </p>
          <p>
            Visit our community portal at community.hrcopilot.com to get started, or access the community directly from your Humanex dashboard. We can't wait to welcome you to the community!
          </p>
        </section>
      </div>

      {/* <footer className="community-footer">
        <p>© 2025 Humanex. All rights reserved.</p>
      </footer> */}
    </div>
  );
}